import { ReactNode } from "react";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import restingFox from "@/assets/gg-fox-resting.jpg.asset.json";
import { AssetImage } from "@/components/system/AssetImage";


export interface ErrorScreenLink {
  label: string;
  description?: string;
  href: string;
  icon?: ReactNode;
}

interface ErrorScreenProps {
  /** Large brand-styled code, e.g. "404" or "500" */
  code: string;
  eyebrow?: string;
  title: string;
  description: string;
  /** Primary / secondary buttons */
  actions?: ReactNode;
  /** Helpful next steps rendered as a link grid */
  links?: ErrorScreenLink[];
  /** Optional technical detail block (error id, stack trace in dev) */
  detail?: ReactNode;
  className?: string;
}

/**
 * Shared esports-professional error surface used by the 404 page and the
 * global error boundary so both read as one designed system rather than
 * two ad-hoc fallbacks.
 */
export function ErrorScreen({
  code,
  eyebrow,
  title,
  description,
  actions,
  links,
  detail,
  className,
}: ErrorScreenProps) {
  return (
    <main
      id="main-content"
      className={cn(
        "relative flex min-h-[calc(100dvh-160px)] items-center justify-center overflow-hidden px-4 py-12",
        className,
      )}
    >
      {/* Ambient arena glow — decorative only */}
      <div aria-hidden className="pointer-events-none absolute inset-0">
        <div className="absolute left-1/2 top-0 h-72 w-72 -translate-x-1/2 rounded-full bg-primary/15 blur-3xl" />
        <div className="absolute bottom-0 right-0 h-64 w-64 rounded-full bg-accent/10 blur-3xl" />
      </div>

      <Card className="card-elevated relative w-full max-w-2xl p-6 text-center sm:p-10">
        <div className="relative mx-auto mb-6 w-full max-w-xs">
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 -z-10 rounded-full bg-primary/15 blur-3xl"
          />
          <AssetImage
            src={restingFox.url}
            alt=""
            aria-hidden
            aspect="16 / 9"
            loading="lazy"
            className="object-cover"
            frameClassName="rounded-lg"
          />
          <div
            aria-hidden
            className="pointer-events-none mx-auto mt-2 h-2 w-2/3 rounded-full bg-foreground/15 blur-md"
          />
        </div>
        {eyebrow && (
          <p className="mb-3 text-[0.7rem] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
            {eyebrow}
          </p>
        )}


        <p
          className="font-display text-display-2xl font-bold leading-none text-primary"
          aria-hidden
        >
          {code}
        </p>

        <h1 className="mt-4 text-balance text-2xl font-semibold tracking-tight sm:text-3xl">
          {title}
        </h1>
        <p className="mx-auto mt-3 max-w-prose text-pretty text-sm text-muted-foreground sm:text-base">
          {description}
        </p>

        {actions && (
          <div className="mt-7 flex flex-col gap-3 sm:flex-row sm:justify-center">
            {actions}
          </div>
        )}

        {links && links.length > 0 && (
          <div className="mt-8 border-t border-border/60 pt-6 text-left">
            <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Helpful next steps
            </p>
            <ul className="grid gap-2 sm:grid-cols-2">
              {links.map((link) => (
                <li key={link.href + link.label}>
                  <a
                    href={link.href}
                    className="flex min-h-11 items-start gap-3 rounded-lg border border-border/60 bg-card/60 px-3 py-2.5 transition-colors hover:border-primary/50 hover:bg-accent/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    {link.icon && (
                      <span className="mt-0.5 text-primary" aria-hidden>
                        {link.icon}
                      </span>
                    )}
                    <span>
                      <span className="block text-sm font-medium">{link.label}</span>
                      {link.description && (
                        <span className="block text-xs text-muted-foreground">
                          {link.description}
                        </span>
                      )}
                    </span>
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}

        {detail && <div className="mt-6 text-left">{detail}</div>}
      </Card>
    </main>
  );
}

export default ErrorScreen;
